<template>
  <div class="cv-lang-toggle archivo-semibold" role="group" aria-label="Idioma / Language">
    <button type="button" :class="{ active: locale === 'pt' }" @click="setLang('pt')">PT</button>
    <button type="button" :class="{ active: locale === 'en' }" @click="setLang('en')">EN</button>
  </div>

  <main class="cv-page">
    <!-- ─── Cabeçalho ─────────────────────────────────────── -->
    <header class="cv-hero">
      <div class="container hero-grid">
        <div class="hero-avatar" v-reveal>
          <img :src="avatar" alt="Gustavo Machado da Silva" />
          <span class="avatar-ring" aria-hidden="true"></span>
        </div>

        <div class="hero-text">
          <span class="eyebrow archivo-semibold">{{ $t('cvPage.eyebrow') }}</span>
          <h1 class="hero-name">
            <span class="hero-first">{{ $t('cvPage.firstName') }}</span>
            <span class="hero-last">{{ $t('cvPage.lastName') }}</span>
          </h1>
          <p class="hero-role inter-regular">{{ $t('cvPage.role') }}</p>
          <p class="hero-location archivo-semibold">{{ $t('cvPage.location') }}</p>

          <div class="hero-contacts">
            <button type="button" class="contact-chip archivo-semibold" @click="copyEmail">
              {{ emailCopied ? $t('cvPage.emailCopied') : contactEmail }}
            </button>
            <a class="contact-chip archivo-semibold" href="https://www.gusmachado.com.br" target="_blank" rel="noopener noreferrer">
              www.gusmachado.com.br
            </a>
          </div>

          <div class="hero-actions">
            <router-link to="/contact" class="btn btn-primary archivo-semibold">
              {{ $t('cvPage.ctaButton') }} <span class="btn-arrow">→</span>
            </router-link>
          </div>
        </div>
      </div>
    </header>

    <!-- ─── Números ───────────────────────────────────────── -->
    <section class="stats" ref="statsRef">
      <div class="container stats-grid">
        <div v-for="stat in stats" :key="stat.key" class="stat">
          <span class="stat-bar" aria-hidden="true"></span>
          <span class="stat-number bebas-neue-regular">
            <span v-if="stat.prefix" class="stat-prefix">{{ stat.prefix }}</span>{{ formatStat(stat) }}<span
              v-if="stat.million"
              class="stat-suffix"
            >{{ $t('cvPage.stats.million') }}</span>
          </span>
          <span class="stat-label archivo-semibold">{{ $t(`cvPage.stats.${stat.key}`) }}</span>
        </div>
      </div>
    </section>

    <!-- ─── Navegação entre seções ────────────────────────── -->
    <nav class="section-nav" aria-label="CV">
      <div class="container section-nav-inner">
        <a
          v-for="section in sections"
          :key="section"
          :href="`#${section}`"
          class="section-link archivo-semibold"
          :class="{ active: activeSection === section }"
          @click.prevent="scrollToSection(section)"
        >
          {{ $t(`cvPage.sections.${section}`) }}
        </a>
      </div>
    </nav>

    <!-- ─── Sobre ─────────────────────────────────────────── -->
    <section id="about" class="cv-section" data-section>
      <div class="container">
        <h2 class="section-title bebas-neue-regular" v-reveal>{{ $t('cvPage.sections.about') }}</h2>
        <div class="about-grid">
          <p
            v-for="(paragraph, index) in aboutParagraphs"
            :key="index"
            class="about-text inter-regular"
            :class="{ lead: index === 0 }"
            v-reveal
          >
            {{ paragraph }}
          </p>
        </div>
      </div>
    </section>

    <!-- ─── Experiência ───────────────────────────────────── -->
    <section id="experience" class="cv-section alt" data-section>
      <div class="container">
        <h2 class="section-title bebas-neue-regular" v-reveal>{{ $t('cvPage.sections.experience') }}</h2>
        <ol class="timeline">
          <li
            v-for="job in experience"
            :key="job.key"
            class="timeline-item"
            :class="{ open: openJob === job.key }"
            v-reveal
          >
            <span class="timeline-dot" aria-hidden="true"></span>
            <button
              type="button"
              class="job-card"
              :aria-expanded="openJob === job.key"
              @click="toggleJob(job.key)"
            >
              <span class="job-period archivo-semibold">
                {{ job.start }} – {{ $t('cvPage.present') }}
              </span>
              <span class="job-company bebas-neue-regular">
                {{ $t(`cvPage.experience.${job.key}.company`) }}
                <small class="archivo-semibold">{{ $t(`cvPage.experience.${job.key}.place`) }}</small>
              </span>
              <span class="job-role inter-regular">{{ $t(`cvPage.experience.${job.key}.role`) }}</span>
              <span class="job-tags archivo-semibold">{{ $t(`cvPage.experience.${job.key}.tags`) }}</span>
              <span class="job-toggle archivo-semibold">
                {{ openJob === job.key ? $t('cvPage.showLess') : $t('cvPage.showMore') }}
                <span class="job-toggle-icon" aria-hidden="true">+</span>
              </span>
            </button>
            <transition name="expand">
              <ul v-show="openJob === job.key" class="job-details inter-regular">
                <li v-for="(point, index) in jobPoints(job.key)" :key="index">{{ point }}</li>
              </ul>
            </transition>
          </li>
        </ol>
        <p class="references inter-regular" v-reveal>{{ $t('cvPage.references') }}</p>
      </div>
    </section>

    <!-- ─── Projetos ──────────────────────────────────────── -->
    <section id="projects" class="cv-section" data-section>
      <div class="container">
        <h2 class="section-title bebas-neue-regular" v-reveal>{{ $t('cvPage.sections.projects') }}</h2>

        <div class="filters" role="tablist">
          <button
            v-for="filter in filters"
            :key="filter"
            type="button"
            role="tab"
            class="filter-chip archivo-semibold"
            :class="{ active: activeFilter === filter }"
            :aria-selected="activeFilter === filter"
            @click="activeFilter = filter"
          >
            {{ $t(`cvPage.filters.${filter}`) }}
            <span class="filter-count">{{ countFor(filter) }}</span>
          </button>
        </div>

        <p class="results-count archivo-semibold" aria-live="polite">
          {{ $t('cvPage.projectCount', filteredProjects.length) }}
        </p>

        <div
          class="carousel"
          @touchstart.passive="projectCarousel.onTouchStart"
          @touchend="projectCarousel.onTouchEnd"
        >
          <div class="carousel-viewport">
            <transition name="swap" mode="out-in">
              <div :key="activeFilter" class="carousel-track" :style="projectCarousel.trackStyle">
                <button
                  v-for="project in filteredProjects"
                  :key="project.id"
                  type="button"
                  class="project-card carousel-item"
                  :aria-label="`${$t('cvPage.openProject')}: ${$t(`cvPage.projects.${project.id}.name`)}`"
                  @click="openProject(project)"
                >
                  <div class="card-media" :class="{ placeholder: !project.image }">
                    <img
                      v-if="project.image"
                      :src="project.image"
                      :alt="$t(`cvPage.projects.${project.id}.name`)"
                      :style="{ objectPosition: project.imagePosition }"
                      loading="lazy"
                    />
                    <span v-else class="placeholder-name bebas-neue-regular" aria-hidden="true">
                      {{ $t(`cvPage.projects.${project.id}.name`) }}
                    </span>
                    <span v-if="project.badge" class="card-badge archivo-semibold">
                      {{ $t(`cvPage.projects.${project.id}.badge`) }}
                    </span>
                  </div>
                  <div class="card-body">
                    <p class="card-meta archivo-semibold">
                      {{ formatPeriod(project.period) }} · {{ $t(`cvPage.projects.${project.id}.place`) }}
                    </p>
                    <h3 class="card-title bebas-neue-regular">{{ $t(`cvPage.projects.${project.id}.name`) }}</h3>
                    <p class="card-type inter-regular">{{ $t(`cvPage.projects.${project.id}.type`) }}</p>
                    <span class="card-cta archivo-semibold">
                      {{ $t('cvPage.openProject') }} <span class="card-cta-arrow">→</span>
                    </span>
                  </div>
                </button>
              </div>
            </transition>
          </div>

          <div v-if="projectCarousel.pages > 1" class="carousel-controls">
            <button type="button" class="carousel-arrow" :aria-label="$t('cvPage.prev')" @click="projectCarousel.prev">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 6 8.5 12l6 6" /></svg>
            </button>
            <button type="button" class="carousel-arrow primary" :aria-label="$t('cvPage.next')" @click="projectCarousel.next">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9.5 6 6 6-6 6" /></svg>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Formação ──────────────────────────────────────── -->
    <section id="education" class="cv-section alt" data-section>
      <div class="container">
        <h2 class="section-title bebas-neue-regular" v-reveal>{{ $t('cvPage.sections.education') }}</h2>
        <div class="edu-grid">
          <article v-for="edu in education" :key="edu.key" class="edu-card" v-reveal>
            <span class="edu-period archivo-semibold">
              {{ edu.start }} – {{ edu.end ?? $t('cvPage.present') }}
            </span>
            <h3 class="edu-school bebas-neue-regular">{{ $t(`cvPage.education.${edu.key}.school`) }}</h3>
            <p class="edu-course inter-regular">{{ $t(`cvPage.education.${edu.key}.course`) }}</p>
            <p class="edu-place archivo-semibold">{{ $t(`cvPage.education.${edu.key}.place`) }}</p>
            <router-link
              v-if="edu.exhibit"
              :to="edu.exhibit"
              class="edu-note inter-regular"
            >
              {{ $t(`cvPage.education.${edu.key}.note`) }} <span class="card-cta-arrow">→</span>
            </router-link>
          </article>
        </div>
      </div>
    </section>

    <!-- ─── Habilidades ───────────────────────────────────── -->
    <section id="skills" class="cv-section" data-section>
      <div class="container">
        <div v-reveal>
          <h2 class="section-title bebas-neue-regular">{{ $t('cvPage.sections.skills') }}</h2>
          <ul class="skill-tiles">
            <li v-for="skill in skills" :key="skill.key" class="skill-tile">
              <span class="skill-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path :d="skill.icon" /></svg>
              </span>
              <span class="skill-name archivo-semibold">{{ $t(`cvPage.skills.${skill.key}`) }}</span>
            </li>
          </ul>
        </div>

        <div class="skills-lower">
          <div v-reveal>
            <h2 class="section-title bebas-neue-regular">{{ $t('cvPage.sections.software') }}</h2>
            <div class="software-groups">
              <div
                v-for="group in softwareGroups"
                :key="group.key"
                class="software-group"
                :class="`tone-${group.tone}`"
              >
                <p class="group-label archivo-semibold">{{ $t(`cvPage.softwareGroups.${group.key}`) }}</p>
                <ul class="tool-list">
                  <li v-for="tool in group.tools" :key="tool.name" class="tool">
                    <span class="tool-mono bebas-neue-regular" aria-hidden="true">{{ tool.abbr }}</span>
                    <span class="tool-name archivo-semibold">{{ tool.name }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div v-reveal>
            <h2 class="section-title bebas-neue-regular">{{ $t('cvPage.sections.languages') }}</h2>
            <ul class="lang-cards">
              <li v-for="lang in languages" :key="lang.key" class="lang-card" :class="`lang-${lang.key}`">
                <span class="lang-code bebas-neue-regular" aria-hidden="true">{{ lang.key.toUpperCase() }}</span>
                <div class="lang-info">
                  <span class="lang-name archivo-semibold">{{ $t(`cvPage.languages.${lang.key}`) }}</span>
                  <span class="lang-level inter-regular">{{ $t(`cvPage.languages.${lang.key}Level`) }}</span>
                  <span class="lang-meter" aria-hidden="true">
                    <span v-for="n in 5" :key="n" :class="{ on: n <= lang.level }"></span>
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Clientes ──────────────────────────────────────── -->
    <section class="cv-section alt clients">
      <div class="container">
        <h2 class="section-title bebas-neue-regular" v-reveal>{{ $t('cvPage.sections.clients') }}</h2>
        <div
          class="carousel"
          v-reveal
          @touchstart.passive="clientCarousel.onTouchStart"
          @touchend="clientCarousel.onTouchEnd"
        >
          <div class="carousel-viewport">
            <ul class="carousel-track client-track" :style="clientCarousel.trackStyle">
              <li v-for="client in clients" :key="client.alt" class="client-logo carousel-item">
                <img :src="client.src" :alt="client.alt" loading="lazy" draggable="false" />
              </li>
            </ul>
          </div>

          <div v-if="clientCarousel.pages > 1" class="carousel-controls">
            <button type="button" class="carousel-arrow" :aria-label="$t('cvPage.prev')" @click="clientCarousel.prev">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 6 8.5 12l6 6" /></svg>
            </button>
            <button type="button" class="carousel-arrow primary" :aria-label="$t('cvPage.next')" @click="clientCarousel.next">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9.5 6 6 6-6 6" /></svg>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── CTA ───────────────────────────────────────────── -->
    <section class="cv-cta">
      <div class="container cta-inner" v-reveal>
        <p class="cta-title bebas-neue-regular">{{ $t('cvPage.ctaTitle') }}</p>
        <router-link to="/contact" class="btn btn-dark archivo-semibold">
          {{ $t('cvPage.ctaButton') }} <span class="btn-arrow">→</span>
        </router-link>
      </div>
    </section>
  </main>

  <FooterGusMachadoYear />

  <!-- ─── Modal do projeto ────────────────────────────────── -->
  <Teleport to="body">
    <transition name="modal">
      <div v-if="selectedProject" class="cv-modal-backdrop" @click.self="closeProject">
        <div class="cv-modal" role="dialog" aria-modal="true" :aria-label="$t(`cvPage.projects.${selectedProject.id}.name`)">
          <button type="button" class="modal-close" :aria-label="$t('cvPage.close')" @click="closeProject">×</button>

          <div
            class="modal-media"
            :class="{
              placeholder: !selectedVideo && !selectedProject.image,
              instagram: isInstagram(selectedVideo)
            }"
          >
            <iframe
              v-if="selectedVideo"
              :key="selectedVideo"
              :src="toEmbedUrl(selectedVideo)"
              :title="$t('cvPage.watchVideo')"
              frameborder="0"
              scrolling="no"
              allow="autoplay; encrypted-media; picture-in-picture; clipboard-write"
              allowfullscreen
            ></iframe>
            <img v-else-if="selectedProject.image" :src="selectedProject.image" :alt="$t(`cvPage.projects.${selectedProject.id}.name`)" />
            <span v-else class="placeholder-name bebas-neue-regular" aria-hidden="true">
              {{ $t(`cvPage.projects.${selectedProject.id}.name`) }}
            </span>
          </div>

          <div class="modal-body">
            <p class="card-meta archivo-semibold">
              {{ $t(`cvPage.groups.${selectedProject.group}`) }} · {{ formatPeriod(selectedProject.period) }}
            </p>
            <h3 class="modal-title bebas-neue-regular">{{ $t(`cvPage.projects.${selectedProject.id}.name`) }}</h3>
            <p class="modal-place archivo-semibold">{{ $t(`cvPage.projects.${selectedProject.id}.place`) }}</p>
            <span v-if="selectedProject.badge" class="card-badge static archivo-semibold">
              {{ $t(`cvPage.projects.${selectedProject.id}.badge`) }}
            </span>
            <p class="modal-type inter-regular">{{ $t(`cvPage.projects.${selectedProject.id}.type`) }}</p>
            <p class="modal-desc inter-regular">{{ $t(`cvPage.projects.${selectedProject.id}.desc`) }}</p>

            <div v-if="selectedProject.specs" class="modal-specs">
              <h4 class="specs-title archivo-semibold">{{ $t('cvPage.specs') }}</h4>
              <ul>
                <li v-for="spec in selectedProject.specs" :key="spec" class="inter-regular">
                  {{ $t(`projectsPage.techInfo.${selectedProject.techKey}.${spec}`) }}
                </li>
              </ul>
            </div>

            <div class="modal-links">
              <a v-if="selectedProject.links?.rcdb" :href="selectedProject.links.rcdb" target="_blank" rel="noopener noreferrer" class="btn btn-ghost archivo-semibold">
                {{ $t('projectsPage.rcdb') }}
              </a>
              <a v-if="selectedProject.links?.hotel" :href="selectedProject.links.hotel" target="_blank" rel="noopener noreferrer" class="btn btn-ghost archivo-semibold">
                {{ $t('projectsPage.hotel') }}
              </a>
              <router-link
                v-if="selectedProject.exhibit"
                :to="selectedProject.exhibit"
                class="btn btn-primary archivo-semibold"
                @click="closeProject"
              >
                {{ $t('cvPage.viewExhibit') }} <span class="btn-arrow">→</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import FooterGusMachadoYear from '@/components/Footer-GusMachadoYear.vue'

import avatar from '@/assets/logo-rs-gusmachado.jpg'
import benditoCampos from '@/assets/carrossel-projetos/bendito-campos.jpg'
import benditoLindoia from '@/assets/carrossel-projetos/bendito-lindoia.jpg'
import benAzelart from '@/assets/carrossel-projetos/ben-azelart-coaster.jpg'
import eurekaImg from '@/assets/carrossel-projetos/eureka.jpg'
import contratempoImg from '@/assets/carrossel-projetos/contratempo.jpg'
import omashuCover from '@/assets/omashu/historia/construcao-geral.jpg'
import dreamsImg from '@/assets/cv/dreams.jpg'
import horusImg from '@/assets/cv/horus.jpg'
import mistralImg from '@/assets/cv/mistral.jpg'
import enigmaukaImg from '@/assets/cv/enigmauka.jpg'
import typhoonImg from '@/assets/cv/typhoon-360.jpg'

import netflixLogo from '@/assets/cv/marcas/Netflix.png'
import redBullLogo from '@/assets/cv/marcas/RedBull.png'
import einhellLogo from '@/assets/cv/marcas/Einhell.png'
import dorflexLogo from '@/assets/cv/marcas/Dorflex.png'
import kanitLogo from '@/assets/cv/marcas/Kanit.png'
import havanLogo from '@/assets/cv/marcas/Havan.png'
import desktopLogo from '@/assets/cv/marcas/Desktop.png'
import ressoLogo from '@/assets/cv/marcas/Resso.png'
import banggoodLogo from '@/assets/cv/marcas/Banggood.png'

const { t, tm, rt, locale } = useI18n()

const setLang = (lang) => {
  locale.value = lang
  localStorage.setItem('lang', lang)
}

// ─── Dados de contato ─────────────────────────────────────
const contactEmail = 'contato@gusmachado.com.br'

const emailCopied = ref(false)
let copiedTimer = 0

const copyEmail = async () => {
  try {
    await navigator.clipboard.writeText(contactEmail)
  } catch {
    window.location.href = `mailto:${contactEmail}`
    return
  }
  emailCopied.value = true
  clearTimeout(copiedTimer)
  copiedTimer = setTimeout(() => { emailCopied.value = false }, 2000)
}

// ─── Números ──────────────────────────────────────────────
const idadeAtual = (() => {
  const hoje = new Date()
  const anoNascimento = 2002
  const mesNascimento = 6
  const diaNascimento = 23

  let idade = hoje.getFullYear() - anoNascimento
  const aindaNaoFezAniversario =
    hoje.getMonth() < mesNascimento ||
    (hoje.getMonth() === mesNascimento && hoje.getDate() < diaNascimento)

  if (aindaNaoFezAniversario) idade -= 1

  return idade
})()

const stats = [
  { key: 'age', value: idadeAtual, prefix: '' },
  { key: 'coasters', value: 6, prefix: '' },
  { key: 'followers', value: 2.5, decimals: 1, prefix: '+', million: true },
  { key: 'views', value: 250, prefix: '+', million: true }
]

const statsRef = ref(null)
const statsProgress = ref(0)

const formatStat = (stat) => {
  const current = stat.value * statsProgress.value
  return current.toLocaleString(locale.value === 'pt' ? 'pt-BR' : 'en-US', {
    minimumFractionDigits: stat.decimals ?? 0,
    maximumFractionDigits: stat.decimals ?? 0
  })
}

const animateStats = () => {
  const DURATION = 1400
  const start = performance.now()
  const tick = (now) => {
    const progress = Math.min(1, (now - start) / DURATION)
    statsProgress.value = 1 - Math.pow(1 - progress, 3)
    if (progress < 1) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

// ─── Sobre / Experiência ──────────────────────────────────
const aboutParagraphs = computed(() => tm('cvPage.about').map((item) => rt(item)))

const experience = [
  { key: 'cacauPark', start: 2025 },
  { key: 'cacauShow', start: 2024 },
  { key: 'gusMachado', start: 2020 }
]

const openJob = ref('cacauPark')
const toggleJob = (key) => {
  openJob.value = openJob.value === key ? '' : key
}
const jobPoints = (key) => tm(`cvPage.experience.${key}.points`).map((item) => rt(item))

// ─── Projetos ─────────────────────────────────────────────
const specKeys = ['speed', 'extension', 'height']

const projects = [
  {
    id: 'dreams',
    group: 'cacauPark',
    period: [2025, null],
    badge: true,
    image: dreamsImg,
    imagePosition: 'center 60%',
    // Vídeo diferente para cada idioma do site
    video: {
      pt: 'https://www.instagram.com/p/DZcxf4YluC3/',
      en: 'https://www.instagram.com/p/DcRW5xQISOG/'
    }
  },
  {
    id: 'mistral',
    group: 'cacauPark',
    period: [2025, null],
    image: mistralImg,
    imagePosition: 'center 35%'
  },
  {
    id: 'horus',
    group: 'cacauPark',
    period: [2025, null],
    image: horusImg,
    // Foto vertical: enquadra o trilho no corte horizontal do card
    imagePosition: 'center 30%'
  },
  {
    id: 'typhoon',
    group: 'cacauPark',
    period: [2026, 2026],
    badge: true,
    image: typhoonImg,
    imagePosition: 'center 55%',
    video: 'https://www.instagram.com/p/DaTpYzhiWcJ/'
  },
  {
    id: 'enigmauka',
    group: 'cacauPark',
    period: [2025, 2026],
    badge: true,
    image: enigmaukaImg,
    imagePosition: 'center 45%'
  },
  {
    id: 'benditoCampos',
    group: 'designer',
    period: [2024, 2025],
    image: benditoCampos,
    video: 'https://www.youtube.com/shorts/TEUZ0Y4jNuc',
    techKey: 'oompamobile2',
    specs: specKeys,
    links: { hotel: 'https://www.benditocacaoresort.com.br/', rcdb: 'https://rcdb.com/22982.htm' }
  },
  {
    id: 'benditoLindoia',
    group: 'designer',
    period: [2024, 2024],
    image: benditoLindoia,
    video: 'https://www.youtube.com/watch?v=Fv61MQ5k7no',
    techKey: 'oompamobile1',
    specs: specKeys,
    links: { hotel: 'https://www.benditocacaoresort.com.br/', rcdb: 'https://rcdb.com/22433.htm' }
  },
  {
    id: 'omashu',
    group: 'independent',
    period: [2026, 2026],
    image: omashuCover,
    video: 'https://www.youtube.com/watch?v=f59654RmbIc',
    techKey: 'omashuExpress',
    specs: specKeys,
    exhibit: '/exposicoes/omashu-express-unip'
  },
  {
    id: 'benAzelart',
    group: 'independent',
    period: [2024, 2024],
    image: benAzelart,
    video: 'https://www.youtube.com/watch?v=02ex9hBiXNI',
    techKey: 'benazelart',
    specs: specKeys
  },
  {
    id: 'eureka',
    group: 'independent',
    period: [2022, 2023],
    image: eurekaImg,
    video: 'https://www.youtube.com/shorts/N2pICVB5oh4',
    techKey: 'eurekaV1',
    specs: specKeys
  },
  {
    id: 'contratempo',
    group: 'independent',
    period: [2018, 2021],
    image: contratempoImg,
    video: 'https://www.youtube.com/watch?v=QtOuFWwOsvU',
    techKey: 'contratempo1',
    specs: specKeys
  }
]

const filters = ['all', 'cacauPark', 'designer', 'independent']
const activeFilter = ref('all')

const filteredProjects = computed(() =>
  activeFilter.value === 'all'
    ? projects
    : projects.filter((project) => project.group === activeFilter.value)
)

const countFor = (filter) =>
  filter === 'all' ? projects.length : projects.filter((project) => project.group === filter).length

// ─── Carrosséis ───────────────────────────────────────────
// perViewFor define quantos itens aparecem por vez conforme a largura da tela
const useCarousel = (count, perViewFor) => {
  const index = ref(0)
  const perView = ref(perViewFor(window.innerWidth))
  const maxIndex = computed(() => Math.max(0, count.value - perView.value))

  const goTo = (i) => {
    index.value = Math.min(Math.max(0, i), maxIndex.value)
  }
  const next = () => {
    index.value = index.value >= maxIndex.value ? 0 : index.value + 1
  }
  const prev = () => {
    index.value = index.value <= 0 ? maxIndex.value : index.value - 1
  }

  const handleResize = () => {
    perView.value = perViewFor(window.innerWidth)
    goTo(index.value)
  }

  let touchStartX = null
  const onTouchStart = (e) => {
    touchStartX = e.touches[0]?.clientX ?? null
  }
  const onTouchEnd = (e) => {
    if (touchStartX === null) return
    const dx = (e.changedTouches[0]?.clientX ?? touchStartX) - touchStartX
    touchStartX = null
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)()
  }

  onMounted(() => window.addEventListener('resize', handleResize))
  onUnmounted(() => window.removeEventListener('resize', handleResize))

  return reactive({
    index,
    pages: computed(() => maxIndex.value + 1),
    trackStyle: computed(() => ({ '--index': index.value, '--per-view': perView.value })),
    goTo,
    next,
    prev,
    onTouchStart,
    onTouchEnd
  })
}

const projectCarousel = useCarousel(
  computed(() => filteredProjects.value.length),
  (width) => (width >= 1024 ? 3 : width >= 680 ? 2 : 1)
)

watch(activeFilter, () => projectCarousel.goTo(0))

const formatPeriod = ([start, end]) => {
  if (start === end) return String(start)
  return `${start} – ${end ?? t('cvPage.present')}`
}

const instagramRegex = /instagram\.com\/(?:p|reel|reels|tv)\/([\w-]+)/

const isInstagram = (url) => !!url && instagramRegex.test(url)

const toEmbedUrl = (url) => {
  const instagram = url.match(instagramRegex)
  if (instagram) return `https://www.instagram.com/p/${instagram[1]}/embed`

  const match = url.match(/(?:v=|shorts\/|youtu\.be\/|embed\/)([\w-]{11})/)
  return match ? `https://www.youtube-nocookie.com/embed/${match[1]}?rel=0&modestbranding=1` : url
}

// O vídeo pode ser um link único ou um link por idioma ({ pt, en })
const selectedVideo = computed(() => {
  const video = selectedProject.value?.video
  if (!video || typeof video === 'string') return video || ''
  return video[locale.value] || video.pt || video.en || ''
})

const selectedProject = ref(null)

const openProject = (project) => {
  selectedProject.value = project
  document.body.style.overflow = 'hidden'
}

const closeProject = () => {
  selectedProject.value = null
  document.body.style.overflow = ''
}

// ─── Formação / Habilidades ───────────────────────────────
const education = [
  { key: 'unip', start: 2025, end: null, exhibit: '/exposicoes/omashu-express-unip' },
  { key: 'mackenzie', start: 2020, end: 2024 }
]

// Ícones de traço em viewBox 24x24
const skills = [
  { key: 'cad', icon: 'M12 3 3.5 7.5v9L12 21l8.5-4.5v-9L12 3Z M3.5 7.5 12 12l8.5-4.5 M12 12v9' },
  { key: 'welding', icon: 'M12 3c1 3.5 5 5.5 5 10a5 5 0 0 1-10 0c0-2.5 1.5-4 2.5-5 .3 1.7 1.2 2.7 2.5 3 0-3-1-5.5 0-8Z' },
  { key: 'machining', icon: 'M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z M12 2.5v3 M12 18.5v3 M2.5 12h3 M18.5 12h3 M5.3 5.3l2.1 2.1 M16.6 16.6l2.1 2.1 M5.3 18.7l2.1-2.1 M16.6 7.4l2.1-2.1' },
  { key: 'scripting', icon: 'M14 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V8l-5-5Z M14 3v5h5 M8.5 13h7 M8.5 16.5h4.5' },
  { key: 'editing', icon: 'M4 5h16v14H4Z M4 9h16 M4 15h16 M8 5v4 M16 5v4 M8 15v4 M16 15v4' },
  { key: 'translation', icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z M3 12h18 M12 3c2.5 2.5 3.5 5.5 3.5 9s-1 6.5-3.5 9 M12 3c-2.5 2.5-3.5 5.5-3.5 9s1 6.5 3.5 9' },
  { key: 'software', icon: 'M8.5 7 3.5 12l5 5 M15.5 7l5 5-5 5 M13.5 4.5l-3 15' }
]

const softwareGroups = [
  {
    key: 'cad',
    tone: 'blue',
    tools: [
      { name: 'Inventor', abbr: 'Iv' },
      { name: 'SolidWorks', abbr: 'Sw' },
      { name: 'AutoCAD', abbr: 'Ac' },
      { name: 'SketchUp', abbr: 'Su' }
    ]
  },
  {
    key: 'creative',
    tone: 'yellow',
    tools: [
      { name: 'Premiere', abbr: 'Pr' },
      { name: 'Photoshop', abbr: 'Ps' }
    ]
  },
  {
    key: 'code',
    tone: 'green',
    tools: [
      { name: 'JavaScript', abbr: 'JS' },
      { name: 'Python', abbr: 'Py' },
      { name: 'Excel', abbr: 'Xl' }
    ]
  }
]

// level: barras preenchidas de 5 no indicador
const languages = [
  { key: 'pt', level: 5 },
  { key: 'en', level: 4 }
]

const clients = [
  { src: netflixLogo, alt: 'Netflix' },
  { src: redBullLogo, alt: 'RedBull' },
  { src: havanLogo, alt: 'Havan' },
  { src: einhellLogo, alt: 'Einhell' },
  { src: dorflexLogo, alt: 'Dorflex' },
  { src: kanitLogo, alt: 'Kanit' },
  { src: desktopLogo, alt: 'Desktop' },
  { src: ressoLogo, alt: 'Resso' },
  { src: banggoodLogo, alt: 'Banggood' }
]

const clientCarousel = useCarousel(
  computed(() => clients.length),
  (width) => (width >= 1024 ? 5 : width >= 680 ? 4 : 2)
)

// ─── Navegação entre seções ───────────────────────────────
const sections = ['about', 'experience', 'projects', 'education', 'skills']
const activeSection = ref('about')

const scrollToSection = (id) => {
  const el = document.getElementById(id)
  if (!el) return
  // Desconta a altura do menu de seções, que fica fixo no topo
  const navHeight = document.querySelector('.section-nav')?.offsetHeight ?? 0
  const offset = navHeight + 16
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' })
}

// ─── Animações de entrada ─────────────────────────────────
const revealObserver = typeof IntersectionObserver !== 'undefined'
  ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          revealObserver.unobserve(entry.target)
        }
      })
    }, { threshold: 0.12 })
  : null

const vReveal = {
  mounted: (el) => {
    el.classList.add('reveal')
    revealObserver?.observe(el)
  },
  unmounted: (el) => revealObserver?.unobserve(el)
}


let sectionObserver
let statsObserver

const handleKeydown = (e) => {
  if (e.key === 'Escape' && selectedProject.value) closeProject()
}

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'auto' })

  if (!revealObserver) {
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'))
  }

  sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) activeSection.value = entry.target.id
    })
  }, { rootMargin: '-45% 0px -50% 0px' })
  document.querySelectorAll('[data-section]').forEach((el) => sectionObserver.observe(el))

  statsObserver = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      animateStats()
      statsObserver.disconnect()
    }
  }, { threshold: 0.4 })
  if (statsRef.value) statsObserver.observe(statsRef.value)

  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  revealObserver?.disconnect()
  sectionObserver?.disconnect()
  statsObserver?.disconnect()
  window.removeEventListener('keydown', handleKeydown)
  clearTimeout(copiedTimer)
  document.body.style.overflow = ''
})
</script>

<style scoped>
.cv-page {
  min-height: 100vh;
  text-align: left;
  background: var(--color-bg-dark);
  color: var(--color-light);
}

.container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ─── Animação de entrada ─────────────────────────────── */
.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.reveal.is-visible {
  opacity: 1;
  transform: none;
}

/* ─── Cabeçalho ───────────────────────────────────────── */
/* ─── Seletor de idioma ───────────────────────────────── */
.cv-lang-toggle {
  position: fixed;
  top: 12px;
  right: 16px;
  z-index: 10;
  display: flex;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(30, 42, 58, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(229, 231, 235, 0.14);
}

.cv-lang-toggle button {
  padding: 9px 16px;
  border: 0;
  background: transparent;
  color: rgba(229, 231, 235, 0.6);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.cv-lang-toggle button:hover {
  color: var(--color-light);
}

.cv-lang-toggle button.active:first-child {
  background: #16A34A;
  color: #FBBF24;
}

.cv-lang-toggle button.active:last-child {
  background: #DC2626;
  color: #2563EB;
}

.cv-hero {
  padding: 96px 0 64px;
  background:
    radial-gradient(ellipse 70% 70% at 90% 0%, rgba(37, 99, 235, 0.3), transparent 70%),
    radial-gradient(ellipse 60% 50% at 0% 100%, rgba(251, 191, 36, 0.1), transparent 70%),
    var(--color-bg-dark);
  border-bottom: 1px solid rgba(229, 231, 235, 0.08);
}

.hero-grid {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 56px;
  align-items: center;
}

.hero-avatar {
  position: relative;
  width: 260px;
  aspect-ratio: 1;
}

.hero-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
  position: relative;
  z-index: 1;
  transition: transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.hero-avatar:hover img {
  transform: scale(1.04) rotate(-2deg);
}

.avatar-ring {
  position: absolute;
  inset: -10px;
  border-radius: 50%;
  border: 2px dashed rgba(251, 191, 36, 0.6);
  animation: spin 30s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.eyebrow {
  display: flex;
  align-items: center;
  color: var(--color-yellow);
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 18px;
}

.eyebrow::before {
  content: "";
  flex: 0 0 auto;
  width: 24px;
  height: 2px;
  margin-right: 10px;
  background: currentColor;
}

.hero-name {
  display: flex;
  flex-direction: column;
  margin: 0 0 14px;
  font-family: "Bebas Neue", sans-serif;
  font-weight: 400;
  line-height: 0.9;
}

.hero-first {
  font-size: 120px;
  color: var(--color-yellow);
  transition: text-shadow 0.35s ease;
}

.hero-name:hover .hero-first {
  text-shadow: 0 0 32px rgba(251, 191, 36, 0.35);
}

.hero-last {
  font-size: 64px;
  color: var(--color-light);
}

.hero-role {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 300;
  color: rgba(229, 231, 235, 0.8);
}

.hero-location {
  margin: 0 0 24px;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(229, 231, 235, 0.5);
}

.hero-contacts {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 24px;
}

.contact-chip {
  display: inline-flex;
  align-items: center;
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid rgba(229, 231, 235, 0.18);
  background: rgba(229, 231, 235, 0.06);
  color: var(--color-light);
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.contact-chip:hover {
  border-color: var(--color-yellow);
  background: rgba(251, 191, 36, 0.1);
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 22px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.btn:hover {
  transform: scale(1.04);
}

.btn-primary {
  background: var(--color-yellow);
  color: var(--color-bg-dark);
}

.btn-ghost {
  background: transparent;
  border-color: rgba(229, 231, 235, 0.35);
  color: var(--color-light);
}

.btn-ghost:hover {
  border-color: var(--color-light);
}

.btn-dark {
  background: var(--color-bg-dark);
  color: var(--color-yellow);
}

.btn-arrow {
  transition: transform 0.25s ease;
}

.btn:hover .btn-arrow {
  transform: translateX(4px);
}

/* ─── Números ─────────────────────────────────────────── */
.stats {
  position: relative;
  background:
    radial-gradient(ellipse 50% 120% at 100% 50%, rgba(37, 99, 235, 0.1), transparent 70%),
    var(--color-light);
  color: var(--color-bg-dark);
}

.stats::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--color-yellow), var(--color-blue));
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding-top: 44px;
  padding-bottom: 44px;
}

.stat {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 6px 28px;
  border-left: 1px solid rgba(30, 42, 58, 0.14);
  cursor: default;
}

.stat:first-child {
  border-left: 0;
  padding-left: 0;
}

.stat-bar {
  width: 28px;
  height: 4px;
  margin-bottom: 14px;
  border-radius: 2px;
  background: var(--color-yellow);
  transition: width 0.35s ease, background 0.35s ease;
}

.stat:hover .stat-bar {
  width: 64px;
  background: var(--color-blue);
}

.stat-number {
  display: flex;
  align-items: baseline;
  font-size: 72px;
  line-height: 0.9;
  font-variant-numeric: tabular-nums;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: left bottom;
}

.stat:hover .stat-number {
  transform: scale(1.06);
}

.stat-prefix {
  font-size: 0.6em;
  margin-right: 2px;
  color: var(--color-blue);
}

.stat-suffix {
  font-size: 0.45em;
  margin-left: 6px;
  color: var(--color-blue);
  letter-spacing: 0.04em;
}

.stat-label {
  margin-top: 10px;
  max-width: 220px;
  font-size: 12px;
  line-height: 1.45;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(30, 42, 58, 0.7);
}

/* ─── Navegação de seções ─────────────────────────────── */
.section-nav {
  position: sticky;
  top: 0;
  z-index: 5;
  background: rgba(30, 42, 58, 0.85);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(229, 231, 235, 0.08);
}

.section-nav-inner {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-right: 120px;
}

.section-nav-inner::-webkit-scrollbar {
  display: none;
}

.section-link {
  flex: 0 0 auto;
  padding: 8px 14px;
  border-radius: 999px;
  color: rgba(229, 231, 235, 0.6);
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  transition: color 0.2s ease, background 0.2s ease;
}

.section-link:hover {
  color: var(--color-light);
}

.section-link.active {
  background: var(--color-yellow);
  color: var(--color-bg-dark);
}

/* ─── Seções ──────────────────────────────────────────── */
.cv-section {
  padding: 72px 0;
}

.cv-section.alt {
  background: #16202D;
}

.section-title {
  margin: 0 0 28px;
  font-size: 56px;
  font-weight: 400;
  line-height: 1;
  color: var(--color-light);
}

/* Sobre */
.about-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px 40px;
}

.about-text {
  margin: 0;
  font-size: 17px;
  font-weight: 300;
  line-height: 1.65;
  color: rgba(229, 231, 235, 0.8);
}

.about-text.lead {
  grid-column: 1 / -1;
  font-size: 26px;
  line-height: 1.4;
  color: var(--color-light);
}

/* Experiência */
.timeline {
  position: relative;
  list-style: none;
  margin: 0;
  padding: 0 0 0 32px;
}

.timeline::before {
  content: "";
  position: absolute;
  left: 7px;
  top: 8px;
  bottom: 8px;
  width: 2px;
  background: linear-gradient(var(--color-yellow), rgba(37, 99, 235, 0.6));
}

.timeline-item {
  position: relative;
  margin-bottom: 18px;
}

.timeline-dot {
  position: absolute;
  left: -32px;
  top: 26px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-bg-dark);
  border: 3px solid var(--color-yellow);
  transition: background 0.25s ease, transform 0.25s ease;
}

.timeline-item.open .timeline-dot {
  background: var(--color-yellow);
  transform: scale(1.2);
}

.job-card {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 16px;
  padding: 22px 24px;
  border-radius: 18px;
  border: 1px solid rgba(229, 231, 235, 0.08);
  background: var(--color-bg-dark);
  color: var(--color-light);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.3s ease, transform 0.3s ease;
}

.job-card:hover {
  border-color: rgba(251, 191, 36, 0.45);
  transform: translateX(4px);
}

.timeline-item.open .job-card {
  border-color: rgba(251, 191, 36, 0.45);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.job-period {
  grid-column: 1 / -1;
  color: var(--color-yellow);
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.job-company {
  grid-column: 1;
  font-size: 40px;
  line-height: 1;
}

.job-company small {
  margin-left: 10px;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(229, 231, 235, 0.5);
  vertical-align: middle;
}

.job-role {
  grid-column: 1;
  font-size: 17px;
  font-weight: 400;
}

.job-tags {
  grid-column: 1;
  font-size: 13px;
  color: #A9C2F7;
}

.job-toggle {
  grid-column: 2;
  grid-row: 2 / 5;
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(229, 231, 235, 0.6);
}

.job-toggle-icon {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(229, 231, 235, 0.1);
  color: var(--color-yellow);
  font-size: 18px;
  transition: transform 0.3s ease;
}

.timeline-item.open .job-toggle-icon {
  transform: rotate(45deg);
}

.job-details {
  margin: 0;
  padding: 18px 24px 22px 44px;
  border: 1px solid rgba(251, 191, 36, 0.45);
  border-top: 0;
  border-radius: 0 0 18px 18px;
  background: rgba(30, 42, 58, 0.6);
  font-size: 15px;
  font-weight: 300;
  line-height: 1.6;
  color: rgba(229, 231, 235, 0.8);
}

.job-details li::marker {
  color: var(--color-yellow);
}

.references {
  margin: 24px 0 0;
  font-size: 14px;
  color: rgba(229, 231, 235, 0.5);
}

.expand-enter-active,
.expand-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Projetos */
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid rgba(229, 231, 235, 0.18);
  background: rgba(229, 231, 235, 0.06);
  color: var(--color-light);
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.filter-chip:hover {
  border-color: rgba(229, 231, 235, 0.4);
}

.filter-chip.active {
  background: var(--color-blue);
  border-color: var(--color-blue);
}

.filter-count {
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(229, 231, 235, 0.14);
  font-size: 11px;
}

.results-count {
  margin: 16px 0 24px;
  color: rgba(229, 231, 235, 0.5);
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

/* Carrossel (projetos e marcas) */
.carousel-viewport {
  overflow: hidden;
  /* espaço extra para o hover e a sombra dos cards não serem cortados */
  padding: 10px 0 30px;
  margin: -10px 0 -14px;
}

.carousel-track {
  --gap: 24px;
  display: flex;
  gap: var(--gap);
  margin: 0;
  padding: 0;
  list-style: none;
  transform: translateX(calc(var(--index) * -1 * (100% + var(--gap)) / var(--per-view)));
  transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
}

.carousel-item {
  flex: 0 0 calc((100% - var(--gap) * (var(--per-view) - 1)) / var(--per-view));
  min-width: 0;
}

.carousel-controls {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}

.carousel-arrow {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 10px;
  border: 1px solid rgba(229, 231, 235, 0.3);
  background: transparent;
  color: var(--color-light);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.carousel-arrow svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.25;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: transform 0.25s ease;
}

.carousel-arrow:hover {
  border-color: var(--color-light);
  background: rgba(229, 231, 235, 0.08);
}

.carousel-arrow:hover svg {
  transform: translateX(-3px);
}

.carousel-arrow.primary {
  border-color: var(--color-yellow);
  background: var(--color-yellow);
  color: var(--color-bg-dark);
}

.carousel-arrow.primary:hover {
  border-color: var(--color-light);
  background: var(--color-light);
}

.carousel-arrow.primary:hover svg {
  transform: translateX(3px);
}

.carousel-arrow:active {
  transform: scale(0.94);
}

.client-track {
  --gap: 14px;
}

.swap-enter-active,
.swap-leave-active {
  transition: opacity 0.25s ease;
}

.swap-enter-from,
.swap-leave-to {
  opacity: 0;
}

.project-card {
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  border-radius: 18px;
  background: #16202D;
  border: 1px solid rgba(229, 231, 235, 0.08);
  color: inherit;
  text-align: left;
  font: inherit;
  cursor: pointer;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

.project-card:hover {
  transform: translateY(-6px);
  border-color: rgba(251, 191, 36, 0.45);
  box-shadow: 0 24px 48px -20px rgba(0, 0, 0, 0.7);
}

.card-media {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
}

.card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s ease;
}

.project-card:hover .card-media img {
  transform: scale(1.06);
}

.card-media::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 50%, rgba(22, 32, 45, 0.9) 100%);
}

.placeholder,
.modal-media.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(ellipse 80% 80% at 100% 0%, rgba(37, 99, 235, 0.55), transparent 70%),
    radial-gradient(ellipse 70% 60% at 0% 100%, rgba(251, 191, 36, 0.25), transparent 70%),
    #0b1c2d;
}

.placeholder-name {
  position: relative;
  z-index: 1;
  padding: 0 16px;
  font-size: 54px;
  line-height: 1;
  text-align: center;
  color: rgba(229, 231, 235, 0.9);
  transition: transform 0.6s ease;
}

.project-card:hover .placeholder-name {
  transform: scale(1.08);
}

.card-badge {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 2;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--color-yellow);
  color: var(--color-bg-dark);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.card-badge.static {
  position: static;
  display: inline-block;
  margin-bottom: 12px;
}

.card-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 20px 22px 22px;
}

.card-meta {
  margin: 0 0 8px;
  color: var(--color-yellow);
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.card-title {
  margin: 0 0 6px;
  font-size: 32px;
  font-weight: 400;
  line-height: 1;
  color: var(--color-light);
}

.card-type {
  margin: 0 0 18px;
  color: rgba(229, 231, 235, 0.7);
  font-size: 15px;
  font-weight: 300;
  line-height: 1.5;
}

.card-cta {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--color-light);
  font-size: 13px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.card-cta-arrow {
  color: var(--color-yellow);
  display: inline-block;
  transition: transform 0.25s ease;
}

.project-card:hover .card-cta-arrow,
.edu-note:hover .card-cta-arrow {
  transform: translateX(6px);
}

/* Formação */
.edu-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.edu-card {
  padding: 26px;
  border-radius: 18px;
  background: var(--color-bg-dark);
  border: 1px solid rgba(229, 231, 235, 0.08);
  transition: border-color 0.3s ease;
}

.edu-card:hover {
  border-color: rgba(37, 99, 235, 0.6);
}

.edu-period {
  color: var(--color-yellow);
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.edu-school {
  margin: 8px 0 6px;
  font-size: 34px;
  font-weight: 400;
  line-height: 1;
}

.edu-course {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 300;
}

.edu-place {
  margin: 0;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(229, 231, 235, 0.5);
}

.edu-note {
  display: inline-block;
  margin-top: 16px;
  color: #A9C2F7;
  font-size: 14px;
  text-decoration: none;
}

/* Habilidades */
.skill-tiles,
.tool-list,
.lang-cards {
  list-style: none;
  margin: 0;
  padding: 0;
}

.skill-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 14px;
}

.skill-tile {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px 16px;
  border-radius: 14px;
  background: #16202D;
  border: 1px solid rgba(229, 231, 235, 0.08);
  cursor: default;
  transition: transform 0.3s ease, border-color 0.3s ease;
}

.skill-tile:hover {
  transform: translateY(-4px);
  border-color: rgba(251, 191, 36, 0.45);
}

.skill-icon {
  width: 46px;
  height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(251, 191, 36, 0.12);
  color: var(--color-yellow);
  transition: background 0.3s ease, color 0.3s ease;
}

.skill-icon svg {
  width: 24px;
  height: 24px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.75;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.skill-tile:hover .skill-icon {
  background: var(--color-yellow);
  color: var(--color-bg-dark);
}

.skill-name {
  font-size: 14px;
  line-height: 1.3;
  color: var(--color-light);
}

.skills-lower {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 48px;
  margin-top: 56px;
}

/* Softwares */
.software-groups {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.software-group {
  --tone: var(--color-blue);
  --tone-soft: rgba(37, 99, 235, 0.16);
  --tone-text: #A9C2F7;
}

.software-group.tone-yellow {
  --tone: var(--color-yellow);
  --tone-soft: rgba(251, 191, 36, 0.14);
  --tone-text: var(--color-yellow);
}

.software-group.tone-green {
  --tone: #16A34A;
  --tone-soft: rgba(22, 163, 74, 0.16);
  --tone-text: #6EE7A0;
}

.group-label {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(229, 231, 235, 0.55);
}

.group-label::before {
  content: "";
  width: 18px;
  height: 3px;
  border-radius: 2px;
  background: var(--tone);
}

.tool-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.tool {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #16202D;
  border: 1px solid rgba(229, 231, 235, 0.08);
  cursor: default;
  transition: border-color 0.25s ease, transform 0.25s ease;
}

.tool:hover {
  transform: translateY(-3px);
  border-color: var(--tone);
}

.tool-mono {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: var(--tone-soft);
  border: 1px solid var(--tone);
  color: var(--tone-text);
  font-size: 20px;
  line-height: 1;
  transition: background 0.25s ease, color 0.25s ease;
}

.tool:hover .tool-mono {
  background: var(--tone);
  color: var(--color-bg-dark);
}

.tool-name {
  font-size: 14px;
  color: var(--color-light);
}

/* Idiomas (cores iguais ao seletor de idioma) */
.lang-cards {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.lang-card {
  --lang-bg: #16A34A;
  --lang-fg: #FBBF24;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 16px;
  border-radius: 14px;
  background: #16202D;
  border: 1px solid rgba(229, 231, 235, 0.08);
  cursor: default;
  transition: border-color 0.3s ease, transform 0.3s ease;
}

.lang-card.lang-pt {
  --lang-gradient: linear-gradient(160deg, #16A34A, #14532D);
  --lang-stripe: #2563EB;
  --lang-shadow: rgba(20, 83, 45, 0.6);
}

.lang-card.lang-en {
  --lang-bg: #1D4ED8;
  --lang-fg: #FFFFFF;
  --lang-gradient: linear-gradient(160deg, #2563EB, #1E3A8A);
  --lang-stripe: #DC2626;
  --lang-shadow: rgba(30, 58, 138, 0.6);
}

.lang-card:hover {
  transform: translateX(4px);
  border-color: var(--lang-bg);
}

.lang-code {
  flex: 0 0 auto;
  width: 72px;
  height: 72px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: var(--lang-gradient, var(--lang-bg));
  box-shadow: inset 0 -6px 0 var(--lang-stripe);
  color: var(--lang-fg);
  text-shadow: 0 2px 0 var(--lang-shadow);
  font-size: 40px;
  line-height: 1;
}

.lang-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.lang-name {
  font-size: 18px;
  color: var(--color-light);
}

.lang-level {
  font-size: 14px;
  color: rgba(229, 231, 235, 0.6);
}

.lang-meter {
  display: flex;
  gap: 4px;
  margin-top: 8px;
}

.lang-meter span {
  width: 22px;
  height: 5px;
  border-radius: 3px;
  background: rgba(229, 231, 235, 0.15);
}

.lang-meter span.on {
  background: var(--color-yellow);
}

/* Clientes */
.client-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  padding: 22px 26px;
  border-radius: 14px;
  background: var(--color-light);
  transition: transform 0.25s ease;
}

.client-logo:hover {
  transform: scale(1.05);
}

/* Logos recortados (assets/cv/marcas): ocupam toda a área útil do card */
.client-logo img {
  display: block;
  min-width: 0;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: grayscale(1);
  opacity: 0.85;
  transition: filter 0.25s ease, opacity 0.25s ease;
}

.client-logo:hover img {
  filter: none;
  opacity: 1;
}

/* CTA */
.cv-cta {
  background: var(--color-yellow);
  color: var(--color-bg-dark);
}

.cta-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding-top: 48px;
  padding-bottom: 48px;
}

.cta-title {
  margin: 0;
  font-size: 52px;
  line-height: 1;
}

/* ─── Mobile ──────────────────────────────────────────── */
@media (max-aspect-ratio: 3/4), (max-width: 760px) {
  .container {
    padding: 0 16px;
  }

  .cv-hero {
    padding: 72px 0 44px;
  }

  .hero-grid {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .hero-avatar {
    width: 150px;
  }

  .hero-first {
    font-size: 84px;
  }

  .hero-last {
    font-size: 44px;
  }

  .hero-role {
    font-size: 16px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px 0;
    padding-top: 32px;
    padding-bottom: 32px;
  }

  .stat {
    padding: 0 0 0 16px;
  }

  .stat:nth-child(odd) {
    border-left: 0;
    padding-left: 0;
  }

  .stat-bar {
    margin-bottom: 10px;
  }

  .stat-number {
    font-size: 52px;
  }

  .stat-label {
    font-size: 11px;
    letter-spacing: 0.08em;
  }

  .cv-section {
    padding: 52px 0;
  }

  .section-title {
    font-size: 44px;
  }

  .about-grid,
  .edu-grid,
  .skills-lower {
    grid-template-columns: 1fr;
  }

  .skills-lower {
    gap: 40px;
    margin-top: 40px;
  }

  .skill-tiles {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .skill-tile {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 12px;
  }

  .skill-icon {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
  }

  .skill-name {
    font-size: 13px;
  }

  .tool-list {
    grid-template-columns: repeat(2, 1fr);
  }

  .about-text.lead {
    font-size: 21px;
  }

  .timeline {
    padding-left: 24px;
  }

  .timeline::before {
    left: 5px;
  }

  .timeline-dot {
    left: -24px;
    width: 14px;
    height: 14px;
  }

  .job-card {
    grid-template-columns: 1fr;
    padding: 18px;
  }

  .job-company {
    font-size: 32px;
  }

  .job-company small {
    display: block;
    margin: 4px 0 0;
  }

  .job-toggle {
    grid-column: 1;
    grid-row: auto;
    margin-top: 8px;
  }

  .job-details {
    padding: 14px 18px 18px 36px;
  }

  .carousel-track {
    --gap: 16px;
  }

  .client-logo {
    height: 96px;
    padding: 18px 16px;
  }

  .client-track {
    --gap: 10px;
  }

  .carousel-arrow {
    width: 44px;
    height: 44px;
  }

  .cta-title {
    font-size: 40px;
  }
}

/* ─── Impressão / PDF ─────────────────────────────────── */
@media print {
  .cv-lang-toggle,
  :global(section.quote),
  .section-nav,
  .hero-actions,
  .filters,
  .results-count,
  .cv-cta,
  .job-toggle,
  .card-cta,
  .carousel-controls,
  .avatar-ring {
    display: none !important;
  }

  /* No PDF o carrossel vira grade com todos os itens */
  .carousel-viewport {
    overflow: visible;
    padding: 0;
    margin: 0;
  }

  .carousel-track {
    flex-wrap: wrap;
    transform: none !important;
  }

  .project-card.carousel-item {
    flex: 0 0 calc((100% - var(--gap) * 2) / 3);
  }

  .client-logo.carousel-item {
    flex: 0 0 calc((100% - var(--gap) * 4) / 5);
  }

  .cv-page,
  .cv-hero,
  .cv-section,
  .cv-section.alt,
  .stats {
    background: #fff !important;
    color: #1E2A3A !important;
  }

  .cv-hero {
    padding: 0 0 16px;
  }

  .reveal {
    opacity: 1 !important;
    transform: none !important;
  }

  .job-details {
    display: block !important;
  }

  .hero-last,
  .section-title,
  .card-title,
  .job-card,
  .contact-chip,
  .about-text,
  .about-text.lead,
  .job-details,
  .card-type,
  .skill-name,
  .tool-name,
  .lang-name,
  .lang-level {
    color: #1E2A3A !important;
  }

  .job-card,
  .job-details,
  .project-card,
  .skill-tile,
  .tool,
  .lang-card,
  .edu-card {
    background: #fff !important;
    border-color: #cbd5e1 !important;
    break-inside: avoid;
  }

  .hero-first,
  .card-meta,
  .job-period,
  .edu-period {
    color: #2563EB !important;
  }

  .cv-section {
    padding: 20px 0;
  }
}
</style>

<style>
/* Modal teleportado para o body (fora do escopo do componente) */
.cv-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(8, 14, 24, 0.75);
  backdrop-filter: blur(6px);
  text-align: left;
}

.cv-modal {
  position: relative;
  width: min(960px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  border-radius: 20px;
  background: #16202D;
  border: 1px solid rgba(229, 231, 235, 0.12);
  color: var(--color-light);
  box-shadow: 0 40px 80px -20px rgba(0, 0, 0, 0.8);
  scrollbar-width: none;
}

.cv-modal .modal-media {
  min-height: 320px;
  background: #0b1c2d;
}

.cv-modal .modal-media iframe,
.cv-modal .modal-media img {
  width: 100%;
  height: 100%;
  min-height: 320px;
  border: 0;
  display: block;
  object-fit: cover;
}

/* Post do Instagram: formato vertical, com fundo branco do próprio embed */
.cv-modal .modal-media.instagram {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: #fff;
}

.cv-modal .modal-media.instagram iframe {
  max-width: 540px;
  height: 640px;
}

.cv-modal .modal-media.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(ellipse 80% 80% at 100% 0%, rgba(37, 99, 235, 0.55), transparent 70%),
    radial-gradient(ellipse 70% 60% at 0% 100%, rgba(251, 191, 36, 0.25), transparent 70%),
    #0b1c2d;
}

.cv-modal .placeholder-name {
  font-family: "Bebas Neue", sans-serif;
  font-size: 72px;
  color: rgba(229, 231, 235, 0.9);
  text-align: center;
  padding: 0 20px;
}

.cv-modal .modal-body {
  padding: 32px 30px;
}

.cv-modal .card-meta {
  margin: 0 0 8px;
  color: var(--color-yellow);
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.cv-modal .modal-title {
  margin: 0 0 4px;
  font-size: 48px;
  font-weight: 400;
  line-height: 1;
}

.cv-modal .modal-place {
  margin: 0 0 14px;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(229, 231, 235, 0.5);
}

.cv-modal .card-badge {
  display: inline-block;
  margin-bottom: 12px;
  padding: 6px 12px;
  border-radius: 999px;
  background: var(--color-yellow);
  color: var(--color-bg-dark);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.cv-modal .modal-type {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 500;
  color: #A9C2F7;
}

.cv-modal .modal-desc {
  margin: 0 0 20px;
  font-size: 15px;
  font-weight: 300;
  line-height: 1.6;
  color: rgba(229, 231, 235, 0.8);
}

.cv-modal .modal-specs {
  margin-bottom: 22px;
  padding: 16px 18px;
  border-radius: 14px;
  background: rgba(229, 231, 235, 0.05);
  border: 1px solid rgba(229, 231, 235, 0.1);
}

.cv-modal .specs-title {
  margin: 0 0 8px;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-yellow);
}

.cv-modal .modal-specs ul {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  line-height: 1.7;
}

.cv-modal .modal-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.cv-modal .btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.cv-modal .btn:hover {
  transform: scale(1.04);
}

.cv-modal .btn-primary {
  background: var(--color-yellow);
  color: var(--color-bg-dark);
}

.cv-modal .btn-ghost {
  border-color: rgba(229, 231, 235, 0.35);
  color: var(--color-light);
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 50%;
  background: rgba(8, 14, 24, 0.7);
  color: var(--color-light);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease;
}

.modal-close:hover {
  background: var(--color-blue);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-active .cv-modal,
.modal-leave-active .cv-modal {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .cv-modal,
.modal-leave-to .cv-modal {
  transform: translateY(20px) scale(0.97);
}

@media (max-aspect-ratio: 3/4), (max-width: 760px) {
  .cv-modal-backdrop {
    padding: 12px;
    align-items: flex-end;
  }

  .cv-modal {
    grid-template-columns: 1fr;
    max-height: calc(100vh - 24px);
  }

  .cv-modal .modal-media,
  .cv-modal .modal-media iframe,
  .cv-modal .modal-media img {
    min-height: 220px;
  }

  .cv-modal .modal-media.instagram iframe {
    height: 560px;
  }

  .cv-modal .modal-body {
    padding: 22px 18px;
  }

  .cv-modal .modal-title {
    font-size: 38px;
  }
}
</style>
